import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

/**
 * Statusleiste der nativen App an das aktive Design anpassen.
 *
 * iOS: nur die Symbolfarbe (die Leiste liegt als Overlay über dem Glas-Kopf).
 * Android: Symbolfarbe UND Hintergrund. Die Leiste hat dort bis Android 14
 * eine eigene Fläche; deren Farbe kam aus dem nativen Theme, das dem
 * System-Design folgt, nicht dem der App. Im Beta-Test (Sep 2026) blieb sie
 * deshalb hell, während die App dunkel war. Ab Android 15 (edge-to-edge)
 * ignoriert das System die Hintergrundfarbe, dort zählt nur die Symbolfarbe.
 *
 * Der Hintergrund kommt aus `--color-background`, damit auch das
 * Kontrast-Design stimmt, ohne eine zweite Farbliste zu pflegen.
 */
const cssVarToHex = (name, fallback) => {
  try {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    const parts = raw.split(/\s+/).map((n) => parseInt(n, 10));
    if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return fallback;
    return `#${parts.map((n) => n.toString(16).padStart(2, '0')).join('')}`;
  } catch {
    return fallback;
  }
};

export const applyStatusBarTheme = async (resolvedTheme) => {
  if (!Capacitor.isNativePlatform()) return;
  const isDark = resolvedTheme === 'dark';
  try {
    if (Capacitor.getPlatform() === 'android') {
      await StatusBar.setBackgroundColor({
        color: cssVarToHex('--color-background', isDark ? '#0f1117' : '#ffffff'),
      });
    } else {
      // iOS: Overlay-Modus für den Glas-Kopf hinter Notch/Dynamic Island.
      // Gilt schon ab dem Start (scripts/ios-statusbar-overlay.mjs), der
      // Aufruf hier ist nur die Absicherung.
      await StatusBar.setOverlaysWebView({ overlay: true });
    }
    // Dunkles Design -> helle Symbole (Style.Dark), helles Design -> dunkle (Style.Light)
    await StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light });
  } catch (error) {
    console.warn('Failed to set StatusBar style:', error);
  }
};

export default applyStatusBarTheme;
